import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  Button,
  TextField,
  Chip,
  Paper,
  Tooltip,
  CircularProgress,
  Skeleton,
  Alert,
  Collapse,
  LinearProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TodayIcon from "@mui/icons-material/Today";
import RouteIcon from "@mui/icons-material/Route";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PinDropIcon from "@mui/icons-material/PinDrop";
import LocalParkingIcon from "@mui/icons-material/LocalParking";

const pad2 = (n) => String(n).padStart(2, "0");
const toYMD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const clampYMD = (ymd, minYmd, maxYmd) => {
  if (!ymd) return ymd;
  if (minYmd && ymd < minYmd) return minYmd;
  if (maxYmd && ymd > maxYmd) return maxYmd;
  return ymd;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDateIN = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const num = (v) => Number(v || 0);

const StatPill = ({ icon, label, value }) => {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderColor: alpha(theme.palette.primary.main, 0.12),
      }}
    >
      <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

const TripCard = ({
  trip,
  selected,
  onSelect,
  onPlay,
  loadingRoute,
}) => {
  const theme = useTheme();

  return (
    <Paper
      onClick={() => onSelect(trip)}
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: selected
          ? alpha(theme.palette.primary.main, 0.35)
          : alpha(theme.palette.divider, 0.9),
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.06)
          : "background.paper",
        cursor: "pointer",
        transition: "0.15s ease",
        "&:hover": {
          borderColor: selected
            ? alpha(theme.palette.primary.main, 0.45)
            : alpha(theme.palette.text.primary, 0.18),
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <TodayIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
              {formatDateIN(trip.date)}{trip.rideNo ? ` • Ride ${trip.rideNo}` : ""}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={0.75} flexShrink={0}>
            {trip.maxSpeed > 0 && (
              <Chip
                size="small"
                icon={<SpeedIcon sx={{ fontSize: 16 }} />}
                label={`${Math.round(trip.maxSpeed)} km/h`}
                sx={{ fontWeight: 700 }}
                variant="outlined"
              />
            )}
            {trip.stoppages > 0 && (
              <Chip
                size="small"
                icon={<LocalParkingIcon sx={{ fontSize: 16 }} />}
                label={`${trip.stoppages} stops`}
                variant="outlined"
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<RouteIcon sx={{ fontSize: 16 }} />}
            label={`${num(trip.distance).toFixed(1)} km`}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            size="small"
            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            label={formatDuration(trip.duration)}
            variant="outlined"
          />
          {trip.idleTime > 0 && (
            <Chip
              size="small"
              icon={<PinDropIcon sx={{ fontSize: 16 }} />}
              label={`Idle ${formatDuration(trip.idleTime)}`}
              variant="outlined"
            />
          )}
        </Stack>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            onPlay(trip);
          }}
          fullWidth
          variant="contained"
          startIcon={loadingRoute ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          disabled={loadingRoute}
          sx={{ borderRadius: 2, py: 1, fontWeight: 800 }}
        >
          {loadingRoute ? "Loading route..." : "Play Trip"}
        </Button>

        {selected && (
          <Collapse in={selected}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Tip: Click the card to zoom the map to the trip route (once loaded).
            </Typography>
          </Collapse>
        )}
      </Stack>
    </Paper>
  );
};

const InMapHistoryOverlay = ({
  deviceId,
  deviceName,
  onClose,
  map,
  onPlayTrip,
}) => {
  const theme = useTheme();

  const todayYmd = useMemo(() => toYMD(new Date()), []);
  const [startDate, setStartDate] = useState(() => todayYmd);
  const [endDate, setEndDate] = useState(() => todayYmd);

  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [routeLoadingId, setRouteLoadingId] = useState(null);
  const [error, setError] = useState(null);

  const [collapsed, setCollapsed] = useState(false);

  // keep endDate >= startDate, both <= today
  useEffect(() => {
    setStartDate((s) => clampYMD(s, null, todayYmd));
    setEndDate((e) => clampYMD(e, null, todayYmd));
  }, [todayYmd]);

  useEffect(() => {
    // if user moves start after end, adjust
    setEndDate((e) => (e < startDate ? startDate : e));
  }, [startDate]);

  const baseUrl = useMemo(() => {
    return (
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_URL ||
      "https://qylmox.in/api"
    );
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const url = `${baseUrl}/history/${deviceId}/report?startDate=${startDate}&endDate=${endDate}`;

      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "x-auth-token": token || "",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}: ${response.statusText}${txt ? ` — ${txt}` : ""}`);
      }

      const data = await response.json();

      const rides = Array.isArray(data?.rides) ? data.rides : [];
      const dailyData = Array.isArray(data?.dailyData) ? data.dailyData : (Array.isArray(data?.trips) ? data.trips : []);
      if (!rides.length && !dailyData.length) {
        setTrips([]);
        setSelectedTripId(null);
        return;
      }

      const tripList = rides.length
        ? rides.map((ride, idx) => ({
            id: String(ride.id || `ride_${idx}`),
            date: String(ride.date || ""),
            rideNo: Number(ride.rideNo || idx + 1),
            startTime: ride.startTime || `${ride.date}T00:00:00Z`,
            endTime: ride.endTime || `${ride.date}T23:59:59Z`,
            distance: num(ride.distanceRawKm ?? ride.distance),
            duration: num(ride.movingTime),
            maxSpeed: num(ride.maxSpeedRawKmh ?? ride.maxSpeed),
            avgSpeed: num(ride.avgSpeedRawKmh ?? ride.avgSpeed),
            stoppages: num(ride.stoppages),
            idleTime: num(ride.idleTime),
            route: [],
            stats: {},
          }))
        : dailyData.map((day, idx) => ({
            id: `trip_${day.date}_${idx + 1}`,
            date: String(day.date || ""),
            startTime: `${day.date}T00:00:00Z`,
            endTime: `${day.date}T23:59:59Z`,
            distance: num(day.distanceRawKm ?? day.distance),
            duration: num(day.movingTime),
            maxSpeed: num(day.maxSpeedRawKmh ?? day.maxSpeed),
            avgSpeed: num(day.avgSpeedRawKmh ?? day.avgSpeed),
            stoppages: num(day.stoppages),
            idleTime: num(day.idleTime),
            route: [],
            stats: {},
          }));

      setTrips(tripList);

      // auto-select first item (latest if backend sorted; else pick last)
      const first = tripList[0];
      setSelectedTripId(first?.id || null);
    } catch (err) {
      setError(err?.message || "Failed to load history");
      setTrips([]);
      setSelectedTripId(null);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, deviceId, startDate, endDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const fetchTripRoute = useCallback(
    async (trip) => {
      if (!trip || !trip.date) return null;

      setRouteLoadingId(trip.id);
      try {
        const token = localStorage.getItem("token");

        const params = new URLSearchParams();
        params.set("date", trip.date);
        if (trip?.startTime) params.set("startTs", new Date(trip.startTime).toISOString());
        if (trip?.endTime) params.set("endTs", new Date(trip.endTime).toISOString());
        const url = `${baseUrl}/history/${deviceId}/playback?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "x-auth-token": token || "",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error(`Route fetch failed: ${response.status}`);

        const data = await response.json();
        const points = Array.isArray(data.points) ? data.points : [];

        // update trip list immutably
        setTrips((prev) =>
          prev.map((t) =>
            t.id === trip.id
              ? { ...t, route: points, stats: data.stats || {} }
              : t
          )
        );

        return { ...trip, route: points, stats: data.stats || {} };
      } catch (err) {
        console.error("❌ Failed to fetch trip route:", err);
        return null;
      } finally {
        setRouteLoadingId(null);
      }
    },
    [baseUrl, deviceId]
  );

  const selectedTrip = useMemo(
    () => trips.find((t) => t.id === selectedTripId) || null,
    [trips, selectedTripId]
  );

  const zoomToTrip = useCallback(
    (tripWithRoute) => {
      if (!map || !tripWithRoute?.route?.length) return;
      const bounds = new window.google.maps.LatLngBounds();
      tripWithRoute.route.forEach((p) => {
        if (typeof p?.lat === "number" && typeof p?.lng === "number") {
          bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
        }
      });
      try {
        map.fitBounds(bounds);
      } catch {}
    },
    [map]
  );

  const handleSelectTrip = useCallback(
    async (trip) => {
      setSelectedTripId(trip.id);

      // lazy-load route for better UX (optional)
      if (!trip.route || trip.route.length === 0) {
        const updated = await fetchTripRoute(trip);
        if (updated?.route?.length) zoomToTrip(updated);
      } else {
        zoomToTrip(trip);
      }
    },
    [fetchTripRoute, zoomToTrip]
  );

  const handlePlayTrip = useCallback(
    async (trip) => {
      let tripToPlay = trip;

      // ensure route exists
      if (!tripToPlay.route || tripToPlay.route.length === 0) {
        const updated = await fetchTripRoute(tripToPlay);
        if (updated?.route?.length) {
          // small wait for state update not required; use updated directly
          tripToPlay = updated;
        } else {
          console.error("❌ No route data for playback");
          return;
        }
      }

      const points = Array.isArray(tripToPlay.route) ? tripToPlay.route : [];
      const first = points[0];
      const last = points[points.length - 1];
      onPlayTrip?.({
        ...tripToPlay,
        deviceId,
        distance: num(tripToPlay.distance),
        durationSeconds: num(tripToPlay.stats?.movingTime || tripToPlay.duration || 0) + num(tripToPlay.stats?.idleTime || tripToPlay.idleTime || 0),
        durationLabel: formatDuration(num(tripToPlay.stats?.movingTime || tripToPlay.duration || 0) + num(tripToPlay.stats?.idleTime || tripToPlay.idleTime || 0)),
        maxSpeed: num(tripToPlay.stats?.maxSpeedRawKmh || tripToPlay.maxSpeed),
        avgSpeed: num(tripToPlay.stats?.avgSpeedRawKmh || tripToPlay.avgSpeed),
        startLocation: first ? { lat: Number(first.lat), lng: Number(first.lng) } : null,
        endLocation: last ? { lat: Number(last.lat), lng: Number(last.lng) } : null,
      });
      onClose?.();
    },
    [fetchTripRoute, onPlayTrip, onClose]
  );

  const summary = useMemo(() => {
    const totalTrips = trips.length;
    const totalKm = trips.reduce((a, t) => a + num(t.distance), 0);
    const totalMoving = trips.reduce((a, t) => a + (t.duration || 0), 0);
    const maxSpeed = trips.reduce((m, t) => Math.max(m, t.maxSpeed || 0), 0);
    return { totalTrips, totalKm, totalMoving, maxSpeed };
  }, [trips]);

  // Drawer sizes
  const drawerWidth = 380;

  return (
    <>
      {/* MAIN PANEL */}
      <Drawer
        anchor="right"
        open={!collapsed}
        onClose={onClose}
        variant="persistent"
        PaperProps={{
          sx: {
            width: drawerWidth,
            height: "100vh",
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            boxShadow: "0px 12px 32px rgba(0,0,0,0.18)",
            overflow: "hidden",
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2,
            py: 1.75,
            color: "common.white",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }} noWrap>
                Trip History
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                {deviceName || "—"}
              </Typography>
            </Box>

            <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
              <Tooltip title={collapsed ? "Expand" : "Collapse"}>
                <IconButton
                  size="small"
                  onClick={() => setCollapsed(true)}
                  sx={{
                    color: "common.white",
                    bgcolor: alpha("#fff", 0.18),
                    "&:hover": { bgcolor: alpha("#fff", 0.28) },
                    borderRadius: 2,
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{
                    color: "common.white",
                    bgcolor: alpha("#fff", 0.18),
                    "&:hover": { bgcolor: alpha("#fff", 0.28) },
                    borderRadius: 2,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* FILTERS */}
        <Box sx={{ p: 2, bgcolor: "background.default" }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(clampYMD(e.target.value, null, todayYmd))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(clampYMD(e.target.value, startDate, todayYmd))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Stack>

            <Button
              onClick={fetchHistory}
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} /> : <SearchIcon />}
              disabled={loading}
              sx={{ borderRadius: 2, py: 1, fontWeight: 900 }}
              fullWidth
            >
              {loading ? "Searching..." : "Search"}
            </Button>

            {/* QUICK SUMMARY */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.05),
                borderColor: alpha(theme.palette.success.main, 0.18),
              }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "text.primary" }}>
                  Summary
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <StatPill
                    icon={<RouteIcon fontSize="small" />}
                    label="Trips"
                    value={`${summary.totalTrips}`}
                  />
                  <StatPill
                    icon={<RouteIcon fontSize="small" />}
                    label="Distance"
                    value={`${summary.totalKm.toFixed(1)} km`}
                  />
                  <StatPill
                    icon={<AccessTimeIcon fontSize="small" />}
                    label="Moving"
                    value={formatDuration(summary.totalMoving)}
                  />
                  <StatPill
                    icon={<SpeedIcon fontSize="small" />}
                    label="Top speed"
                    value={`${Math.round(summary.maxSpeed || 0)} km/h`}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Box>

        <Divider />

        {/* LIST */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {loading && (
            <Stack spacing={1.2}>
              <LinearProgress />
              {[...Array(4)].map((_, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <Skeleton variant="text" width="60%" />
                    <Stack direction="row" spacing={1}>
                      <Skeleton variant="rounded" width={92} height={28} />
                      <Skeleton variant="rounded" width={88} height={28} />
                      <Skeleton variant="rounded" width={74} height={28} />
                    </Stack>
                    <Skeleton variant="rounded" height={36} />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>Error loading trips</Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {!loading && !error && trips.length === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>No trips found</Typography>
              <Typography variant="body2">Try selecting a different date range.</Typography>
            </Alert>
          )}

          {!loading && !error && trips.length > 0 && (
            <Stack spacing={1.25}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: alpha(theme.palette.primary.main, 0.14),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  ✅ {trips.length} trip{trips.length !== 1 ? "s" : ""} found
                </Typography>
              </Paper>

              {trips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  selected={selectedTripId === trip.id}
                  onSelect={handleSelectTrip}
                  onPlay={handlePlayTrip}
                  loadingRoute={routeLoadingId === trip.id}
                />
              ))}
            </Stack>
          )}
        </Box>

        {/* FOOTER (Selected Trip details) */}
        <Divider />
        <Box sx={{ p: 2, bgcolor: "background.paper" }}>
          {selectedTrip ? (
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }} noWrap>
                Selected: {formatDateIN(selectedTrip.date)}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`Avg ${Math.round(selectedTrip.avgSpeed || 0)} km/h`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Idle ${formatDuration(selectedTrip.idleTime || 0)}`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Stops ${selectedTrip.stoppages || 0}`}
                  variant="outlined"
                />
              </Stack>

              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Click a trip card to auto-load route + zoom on map. Use Play to start playback.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Select a trip to see details.
            </Typography>
          )}
        </Box>
      </Drawer>

      {/* COLLAPSED HANDLE */}
      {collapsed && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            right: 10,
            transform: "translateY(-50%)",
            zIndex: 9999,
          }}
        >
          <Button
            onClick={() => setCollapsed(false)}
            variant="contained"
            startIcon={<ChevronLeftIcon />}
            sx={{
              borderRadius: "14px 0 0 14px",
              py: 1.25,
              px: 1.5,
              fontWeight: 900,
              boxShadow: "0px 10px 22px rgba(0,0,0,0.25)",
            }}
          >
            History
          </Button>
        </Box>
      )}
    </>
  );
};

export default InMapHistoryOverlay;
